import { getItemSummaryShortDescription } from '@/redux/modules/item/summary/itemSummary.selectors';
import { hoverStyles } from '@/components/GridItemCard/Card/components/ItemCardTitle/ItemCardTitle';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { useAppSelector } from '@/redux/hooks';
import { useItemOrPriceResultLink } from '@/hooks/useItemOrPriceResultLink';
import noop from '@/utils/noop';
import React, { ReactElement } from 'react';
import styled, { css } from 'styled-components';

export type DescriptionLinkProps = {
    className?: string;
    increaseLineHeight?: boolean;
    increaseTextSize?: boolean;
    isHovered?: boolean;
    itemId: number;
    lineCount?: number;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    onNavigate?: () => void;
};

const DescriptionLink = ({
    className,
    increaseLineHeight,
    increaseTextSize,
    isHovered,
    itemId,
    lineCount = 3,
    onMouseEnter = noop,
    onMouseLeave = noop,
    onNavigate,
}: DescriptionLinkProps): ReactElement<DescriptionLinkProps> => {
    const shortDescription = useAppSelector((state) => getItemSummaryShortDescription(state, itemId));
    const itemUrl = useItemOrPriceResultLink(itemId, true);

    return (
        <ContainerLink
            $isHovered={isHovered}
            href={itemUrl}
            onClick={() => {
                onNavigate();
            }}
        >
            <StyledDescription
                $increaseLineHeight={increaseLineHeight}
                $increaseTextSize={increaseTextSize}
                $lineCount={lineCount}
                className={className}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
            >
                {shortDescription}
            </StyledDescription>
        </ContainerLink>
    );
};

export default DescriptionLink;

const StyledDescription = styled.p<{
    $increaseLineHeight?: boolean;
    $increaseTextSize?: boolean;
    $lineCount: number;
}>`
    font-family: ${({ theme }) => theme.typography.fontPrimary};
    font-size: 12px;
    line-height: 18px;
    letter-spacing: 0.5px;
    color: ${({ theme }) => theme.colors.grey100};
    margin: 0;

    && {
        -webkit-line-clamp: 3;
        overflow: hidden;
        white-space: normal;

        /* stylelint-disable value-no-vendor-prefix  */
        /* stylelint-disable property-no-vendor-prefix */
        display: -webkit-box;
        -webkit-box-orient: vertical;
        ${({ $lineCount }) =>
            $lineCount &&
            css`
                -webkit-line-clamp: ${$lineCount};
            `}
    }

    ${({ $increaseTextSize }) =>
        $increaseTextSize &&
        css`
            font-size: 16px;
        `}
    ${({ $increaseLineHeight }) =>
        $increaseLineHeight &&
        css`
            line-height: 24px;
        `}
`;

const ContainerLink = styled(Link)<{ $isHovered?: boolean }>`
    cursor: pointer;
    text-decoration: none;
    text-transform: capitalize;

    &:hover p {
        ${hoverStyles}
    }

    ${({ $isHovered }) =>
        $isHovered &&
        css`
            p {
                color: ${({ theme }) => theme.colors.blue200};
                text-decoration: underline;
            }
        `}
`;
